import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRFQ } from '../contexts/RFQContext';
import { trpClient } from '../client/trpClient';
import { calculateWorkflowStep, discoverySubStep } from '../utils/workflowUtils';
import { computeJourneyReadiness, firstIncompleteStep } from '../utils/journeyReadiness';
import { WizardPos } from '../utils/wizardRoutes';
import { StageReport, stageActivityFailed, stageActivityStatus } from '../utils/stageActivity';
import { useIsMobile } from '../hooks/useIsMobile';
import { ROBBIE_RAIL_RESIZE, useResizableRail } from '../hooks/useResizableRail';
import { useWizardRoute } from '../hooks/useWizardRoute';
import { useWizardExport } from '../hooks/useWizardExport';
import { useEndUserHandoff } from '../hooks/useEndUserHandoff';
import { RailResizeHandle, TipFromRobbie, WizardHeader, WizardJourneyBar, WizardJourneyNav } from '../components/wizard';
import { RobbieMark, StepBarSlot, StepBarSlotProvider, ViewOnlyBanner } from '../components/ui';
import { FeedbackButton } from '../components/shell';
import { useFeedback } from '../contexts/FeedbackContext';
import { WizardAccessProvider } from '../contexts/WizardAccessContext';
import { WizardHeaderAsideProvider, WizardHeaderAsideOutlet } from './wizard/WizardHeaderAside';
import { ShareModal } from '../components/share';
import { UploadDocumentsHandle } from '../components/forms/UploadDocuments';
import { LOCKED_STAGES, WIZARD_STEP_META, WIZARD_SUBSTEP_META, buildJourneySteps } from './wizard/journeyModel';
import { nextBestStepFor, robbieContentForStep } from './wizard/robbieStepContent';
import WizardRobbieDocked from './wizard/WizardRobbieDocked';
import WizardRobbieSheet from './wizard/WizardRobbieSheet';
import { useWizardRobbie } from './wizard/useWizardRobbie';
import { useRecordWizardNavigation } from './wizard/useRecordWizardNavigation';
import { useWizardNavigation } from './wizard/useWizardNavigation';
import WizardStepContent from './wizard/WizardStepContent';
import { WizardToastOutlet, WizardToastProvider } from '../components/wizard/WizardToast';
import WizardEndUserStrip from './wizard/WizardEndUserStrip';
import { WizardError, WizardLoading, WizardNotFound } from './wizard/WizardStates';
import { sameRfqId } from '../utils/rfqIdentity';

/**
 * The RFQ wizard under its own v2 chrome: navy header, journey rail driven by
 * the RFQ's workflow state, the step's workspace in the middle, and Robbie
 * docked on the right. The URL names the position (`/rfqs/:rfqId/<step>`);
 * this page renders whatever step the route hands it.
 */
export interface RFQWizardProps {
  /** Step named by the route; omitted on bare `/rfqs/:rfqId` (resolve + redirect). */
  routePos?: WizardPos;
}

const RFQWizardPage: React.FC<RFQWizardProps> = ({ routePos }) => {
  const { rfqId = '' } = useParams<{ rfqId: string }>();
  // A phone has room for the step content or for a rail beside it, not both.
  // Which one it is decides two things here: where the journey lives, and
  // whether Robbie is a column or a button.
  const isMobile = useIsMobile();
  // Robbie's rail width is the user's to drag; remembered per browser.
  const robbieRail = useResizableRail(ROBBIE_RAIL_RESIZE);
  const { currentRFQ, loading, error, fetchRFQ } = useRFQ();
  // The wizard has its own chrome and no top bar, so without this the only way
  // to reach the feedback panel from inside an RFQ was to leave the RFQ.
  const { openFeedback } = useFeedback();

  // Only trust the loaded RFQ once it matches the URL (number or Mongo _id) —
  // never render one RFQ's data under another RFQ's address. The URL carries
  // the display form of the number and the payload the stored one, so the
  // comparison goes through `sameRfqId` rather than `===`.
  const rfq = currentRFQ && (sameRfqId(currentRFQ.rfqId, rfqId) || currentRFQ._id === rfqId) ? currentRFQ : null;
  const pos = useWizardRoute(rfq, rfqId, routePos);
  // The RFQ's creator and anyone they shared it with as an EDITOR may act on
  // it; everyone else in the org reads the whole thing. Only the OWNER may
  // change who has access, which is why `role` is read as well as `canEdit`.
  // The server decides — a payload without the flags (an older cached
  // response) stays editable and owned so the owner is never locked out.
  const readOnly = rfq?.access?.canEdit === false;
  const role = rfq?.access?.role ?? 'OWNER';
  const ownerName = rfq?.access?.ownerName ?? null;
  const [shareOpen, setShareOpen] = useState(false);
  const reached = rfq ? calculateWorkflowStep(rfq) : { step: 1, subStep: 1 };
  // Data-truth for the rail and click gating: what has actually been
  // accomplished (BOM, criteria, shortlist, outreach, award) — navigation
  // never marks a step done. The frontier also includes the workflow's own
  // record of the furthest step reached: revisiting an earlier step must
  // never lock the user out of returning to where they already were.
  const readiness = computeJourneyReadiness(rfq);
  const journeyFrontier = Math.max(firstIncompleteStep(readiness), pos.step, reached.step);

  // View-only strip for the RFQ's end user on every step except Discovery,
  // where the sub-step panels carry their own richer handoff banners.
  const handoff = useEndUserHandoff(rfq);

  const uploadDocsRef = useRef<UploadDocumentsHandle>(null);
  const mainRef = useRef<HTMLElement>(null);
  const { exporting, exportFile } = useWizardExport(rfq?.rfqId);
  // Which stage is mid-flight, reported by whichever step is on screen. Robbie
  // offers no next best step while one is set — see utils/stageActivity.
  const [stageActivity, setStageActivity] = useState<StageReport | null>(null);
  // When Robbie changes data via a tool call, refresh the RFQ in place so the
  // on-screen form reflects it without moving the user off their current step.
  const refreshRfq = useCallback(() => {
    if (rfq) fetchRFQ(rfq.rfqId, true, true);
  }, [rfq, fetchRFQ]);
  // One conversation for both of Robbie's shapes; steps send through it
  // ("Enhance with Robbie", "Why these suppliers?") on the buyer's behalf.
  const robbie = useWizardRobbie(rfq?.rfqId, pos.step, refreshRfq, isMobile, pos.subStep);
  useRecordWizardNavigation(rfqId, rfq ? pos : null);

  // Re-read the RFQ on every step change, not only on mount. Steps write
  // straight to the BOM (shortlisting on step 3, sends on step 4) without
  // touching this snapshot, so a rail fed by the mount-time copy would keep
  // calling finished work unfinished until a reload. Silent once the RFQ is
  // loaded — moving between steps is not a page load.
  const loadedRfqRef = useRef<string | null>(null);
  useEffect(() => {
    if (!rfqId) return;
    const silent = loadedRfqRef.current === rfqId;
    loadedRfqRef.current = rfqId;
    fetchRFQ(rfqId, silent, true);
  }, [rfqId, pos.step, fetchRFQ]);

  const {
    goTo, handleBackToDashboard, handleContinue, handleBack, handleStepSelect, handleSubStepSelect, handleRobbieAction,
  } = useWizardNavigation({
    rfq, rfqId, pos, readOnly, journeyFrontier, uploadDocsRef, mainRef,
  });

  // Renames edit the stored `name` field — the same one the naming popup sets
  // at creation, so the header and dashboard always agree.
  const handleRename = async (name: string) => {
    if (!rfq || !name || name === rfq.name) return;
    try {
      await trpClient.updateRFQ(rfq.rfqId, { name });
      fetchRFQ(rfq.rfqId, true, true);
    } catch (e) {
      console.error('Failed to update project name', e);
    }
  };

  if (!rfq) {
    if (loading.isLoading) return <WizardLoading message={loading.message} />;
    if (error.hasError) {
      const message = error.message || '';
      const notFound = /not found|404/i.test(message);
      return notFound ? (
        <WizardNotFound rfqId={rfqId} onBack={handleBackToDashboard} />
      ) : (
        <WizardError message={message} onBack={handleBackToDashboard} />
      );
    }
    return <WizardNotFound rfqId={rfqId} onBack={handleBackToDashboard} />;
  }

  // Bare /rfqs/:id — the route hook is about to replace to the current step.
  if (!routePos) return <WizardLoading />;

  const meta =
    (pos.step === 3 ? WIZARD_SUBSTEP_META[pos.subStep] : undefined) ??
    WIZARD_STEP_META[pos.step] ??
    WIZARD_STEP_META[1];
  const content = {
    ...robbieContentForStep(pos.step, pos.subStep),
    nextBestStep: nextBestStepFor(pos, readiness),
  };

  // One set of journey props for both shapes — the phone's bar and the
  // desktop's rail must never be able to describe different journeys.
  const journey = {
    steps: buildJourneySteps(pos, reached, readiness, journeyFrontier),
    locked: LOCKED_STAGES,
    onStepSelect: handleStepSelect,
    onSubStepSelect: handleSubStepSelect,
    tip: <TipFromRobbie>{content.tip}</TipFromRobbie>,
  };

  return (
    <WizardAccessProvider readOnly={readOnly} role={role} ownerName={ownerName}>
      <WizardHeaderAsideProvider>
      <WizardToastProvider>
      <div className="flex h-app flex-col overflow-hidden bg-canvas font-sans">
        <WizardHeader
          name={rfq.name || 'Untitled RFQ'}
          onBack={handleBackToDashboard}
          onRename={readOnly ? undefined : handleRename}
          savedLabel="Saved just now"
          exportItems={[
            { id: 'sourcing-summary', label: 'Sourcing Summary (PDF)', busy: exporting === 'sourcing-summary', onSelect: () => exportFile('sourcing-summary') },
            // The Smart BOM is the product's payoff: it exists as an export only
            // once the BOM has actually become smart — real progress into the
            // discovery sub-step (data truth via the workflow document, never
            // navigation), matching the reveal moment in step 3.
            ...(reached.step > 3 || (reached.step === 3 && discoverySubStep(rfq) >= 3)
              ? [{ id: 'smart-bom', label: 'Smart BOM (Excel)', busy: exporting === 'smart-bom', onSelect: () => exportFile('smart-bom') }]
              : []),
          ]}
          onShare={role === 'OWNER' ? () => setShareOpen(true) : undefined}
          feedbackSlot={<FeedbackButton tone="chrome" onClick={() => openFeedback('wizard')} />}
        />
        {/* On a phone the journey is a line under the header rather than a
            column beside the work — same rail, same states, one tap away. */}
        {isMobile && <WizardJourneyBar {...journey} />}
        <div className="flex flex-1 overflow-hidden">
          {!isMobile && <WizardJourneyNav {...journey} />}
          <div className="relative flex min-w-0 flex-1">
          <main ref={mainRef} className="flex min-w-0 flex-1 flex-col overflow-y-auto">
            <StepBarSlotProvider>
            {/* Fluid canvas per the v2.1 mock — full width up to 1160px so
                lines stay readable on large externals (owner refinement, 29
                Jul: plain width:100%/max-width:1160px, not the 94% component,
                so small screens lose the cap entirely and stay truly fluid;
                padding alone provides the breathing room). Below the cap it
                is fully fluid; above it the cap binds and the canvas centers. */}
            <div className="mx-auto flex w-full max-w-[1160px] flex-1 flex-col gap-[16px] px-[24px] py-[20px] max-md:gap-s5 max-md:px-s5 max-md:py-s5">
              {/* Step content may place one small control on this line via
                  the header-aside slot (e.g. the Smart BOM pill), aligned to
                  the subtitle so it never costs the page an extra row. */}
              <div className="flex items-end justify-between gap-s4">
                <div className="min-w-0">
                  <h1 className="m-0 mb-[5px] text-page-title text-ink">{meta.title}</h1>
                  <p className="m-0 text-body-md text-ink-secondary">{meta.subtitle}</p>
                </div>
                <WizardHeaderAsideOutlet className="mb-[2px]" />
              </div>
              {/* The RFQ's end user gets their own strip — the generic
                  view-only banner says "ask the owner", which is wrong when
                  the technical steps are theirs to fill. Step 3 carries its
                  richer handoff banners instead. */}
              {handoff.isEndUser && pos.step !== 3 && (
                <WizardEndUserStrip canFill={handoff.canFill} onGoToQuestions={() => goTo({ step: 3, subStep: 1 })} />
              )}
              {readOnly && !handoff.isEndUser && <ViewOnlyBanner ownerName={ownerName} />}
              {loading.isLoading && (
                <div className="flex items-center gap-s4 rounded-card border border-robbie-border bg-ai-chip px-[14px] py-[10px]">
                  <RobbieMark size="xs" state="thinking" aria-label="Robbie is working" />
                  <div>
                    <p className="m-0 text-body-sm font-semibold text-ink">Robbie is working…</p>
                    {loading.message && <p className="m-0 text-meta text-ink-secondary">{loading.message}</p>}
                  </div>
                </div>
              )}
              {/* Extra room at the foot on a phone so the last control of a step is
                  never sitting under Robbie's button. On desktop the step's bar
                  follows in the slot below, so the canvas padding is all it needs. */}
              <div className="max-md:pb-[92px]">
                <WizardStepContent
                  step={pos.step}
                  rfq={rfq}
                  readiness={readiness}
                  uploadDocsRef={uploadDocsRef}
                  sendToRobbie={robbie.sendToRobbie}
                  onNext={handleContinue}
                  onBack={handleBack}
                  onCancel={handleBackToDashboard}
                  onWorking={setStageActivity}
                />
              </div>
            </div>
            <StepBarSlot />
            </StepBarSlotProvider>
          </main>
          <WizardToastOutlet />
          </div>
          {!isMobile && (
            <div
              ref={robbieRail.railRef}
              className="relative flex shrink-0"
              style={robbieRail.railStyle}
            >
              <RailResizeHandle label="Resize Robbie's panel" {...robbieRail.handle} />
              <WizardRobbieDocked
                className="min-w-0 flex-1"
                rfqId={rfq.rfqId}
                step={pos.step}
                content={content}
                chat={robbie.chat}
                working={stageActivityStatus(stageActivity)}
                workingFailed={stageActivityFailed(stageActivity)}
                onAction={handleRobbieAction}
                canChat={!readOnly || handoff.canFill}
              />
            </div>
          )}
        </div>
        {isMobile && (
          <WizardRobbieSheet
            rfqId={rfq.rfqId}
            step={pos.step}
            content={content}
            chat={robbie.chat}
            open={robbie.sheetOpen}
            setOpen={robbie.setSheetOpen}
            working={stageActivityStatus(stageActivity)}
            workingFailed={stageActivityFailed(stageActivity)}
            onAction={handleRobbieAction}
            canChat={!readOnly || handoff.canFill}
          />
        )}
        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} rfqId={rfq.rfqId} rfqName={rfq.name} />
      </div>
      </WizardToastProvider>
      </WizardHeaderAsideProvider>
    </WizardAccessProvider>
  );
};

export default RFQWizardPage;
